import { useProjectsCache } from "frontend/hooks/caching/use-projects-cache";
import { useTeamsCache } from "frontend/hooks/caching/use-teams-cache";
import { useState, useEffect } from "react";
import { Project } from "shared/datamodel/schemas/project";
import { Team, TeamWithProjects } from "shared/datamodel/schemas/team";
import { ProjectCellNew } from "./projects-cell-new";
import { CanvasTemplate } from "shared/datamodel/schemas";
import style from "./projects-list.module.css";
import { AddProjectIcon } from "frontend/icons/add-project";
import { ProjectBigIcon } from "frontend/icons/project-big";

export default function ProjectList({
  team,
  selectedProjectId,
  handleProjectSelection,
  onEditSeats,
  createNewBoard,
  onDeleteSelectedProject,
  handleNewProjectClick,
}: {
  team: TeamWithProjects;
  selectedProjectId: string | null;
  handleProjectSelection: (project: Project) => void;
  onEditSeats: () => void;
  createNewBoard: ({
    template,
    inputProjectId,
    inputTeamId,
    source,
  }: {
    template: CanvasTemplate;
    inputProjectId: string | null;
    inputTeamId: string | null;
    source: string;
  }) => void;
  onDeleteSelectedProject: () => void;
  handleNewProjectClick: (teamId: string) => void;
}) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [parentTeam, setParentTeam] = useState<Team | null>(null);
  const [showEmptyState, setShowEmptyState] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { getProjects } = useProjectsCache();
  const { getTeams } = useTeamsCache();

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setIsLoading(true);
        const [fetchedProjects, parentTeams] = await Promise.all([
          getProjects(team.projects.map((project) => project.documentId)),
          getTeams([team.documentId]),
        ]);
        setProjects(fetchedProjects as Project[]);
        setParentTeam(parentTeams[0]);
        if (fetchedProjects.length === 0) {
          setShowEmptyState(true);
        }
        setIsLoading(false);
      } catch {
        setProjects([]);
        setShowEmptyState(true);
        setParentTeam(null);
        setIsLoading(false);
      }
    };

    fetchProjects();
  }, [team.documentId, team.projects, getProjects, getTeams]);

  useEffect(() => {
    if (projects.length > 0) {
      setShowEmptyState(false);
    }
  }, [projects]);

  return (
    <div>
      {parentTeam &&
        projects.map((project) => (
          <ProjectCellNew
            key={`project-${project.documentId}`}
            project={project}
            parentTeam={parentTeam}
            isSelected={selectedProjectId === project.documentId}
            onClick={() => handleProjectSelection(project)}
            onEditSeats={onEditSeats}
            createNewBoard={createNewBoard}
            onDeleteSelectedProject={onDeleteSelectedProject}
          />
        ))}
      {projects.length > 0 && (
        <div
          className={style.addProjectContainer}
          onClick={() => handleNewProjectClick(team.documentId)}
          data-testid="add-project-button"
        >
          <div className={style.addProject}>
            <AddProjectIcon />
            <span> Add Project </span>
          </div>
        </div>
      )}
      {showEmptyState && (
        <div className={style.emptyState}>
          <ProjectBigIcon color={"#AFB3BF"} />
          <p className={style.emptyStateText}>Add projects to your workspace</p>
          <div
            className={style.addProjectButton}
            onClick={() => handleNewProjectClick(team.documentId)}
            data-testid="empty-state-add-project-button"
          >
            <AddProjectIcon />
            <span>Add Project</span>
          </div>
        </div>
      )}
      {isLoading && (
        <div className={style.loading}>
          <div className={style.emptyStateText}>
            Loading projects
            <div className={style.dotTyping}></div>
          </div>
        </div>
      )}
    </div>
  );
}
